import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowRight, Bell, CheckCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, AppNotification, PagedResponse } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { Alert, Button, IconButton, LoadingSkeleton, Overline } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { formatDateTime, formatRelativeShort } from '@/utils/formatters';
import { useIsLandscape } from '@/hooks/useMediaQuery';
import { refreshUnread } from './useUnreadNotifications';
import { onPush } from './push';

const PAGE_SIZE = 20;

const dayGroup = (iso: string, t: (k: 'notifications.today' | 'notifications.yesterday') => string) => {
    const d = new Date(iso);
    const today = new Date();
    const diff = Math.floor((new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86_400_000);
    if (diff <= 0) return t('notifications.today');
    if (diff === 1) return t('notifications.yesterday');
    return d.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'short' }).toUpperCase();
};

/** 16d Notifikasi — grouped list; landscape adds a 400px detail panel. */
const NotificationsPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isLandscape = useIsLandscape();
    const { selectedShop } = useAppSelector((s) => s.auth);
    const [items, setItems] = useState<AppNotification[]>([]);
    const [page, setPage] = useState(0);
    const [last, setLast] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const load = useCallback(async (p: number, replace: boolean) => {
        if (!selectedShop) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get<ApiResponse<PagedResponse<AppNotification>>>(API_ENDPOINTS.NOTIFICATIONS(selectedShop.shopId), {
                params: { page: p, size: PAGE_SIZE, sort: 'createdAt,desc' },
            });
            const content = res.data.data.content || [];
            setItems((prev) => (replace ? content : [...prev, ...content]));
            setLast(res.data.data.page?.last ?? content.length < PAGE_SIZE);
            setPage(p);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [selectedShop]);

    useEffect(() => {
        load(0, true);
        return onPush(() => load(0, true));
    }, [load]);

    const groups = useMemo(() => {
        const map = new Map<string, AppNotification[]>();
        items.forEach((n) => { const g = dayGroup(n.createdAt, t); map.set(g, [...(map.get(g) ?? []), n]); });
        return [...map.entries()];
    }, [items, t]);

    if (!selectedShop) return null;
    const shopId = selectedShop.shopId;
    const unreadCount = items.filter((n) => !n.isRead).length;
    const selected = items.find((n) => n.id === selectedId) ?? null;

    const markRead = async (n: AppNotification) => {
        if (n.isRead) return;
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
        try {
            await api.put(API_ENDPOINTS.NOTIFICATION_READ(shopId), null, { params: { notificationId: n.id } });
            refreshUnread(shopId);
        } catch (err) {
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: false } : x)));
            toast.error(getErrorMessage(err));
        }
    };

    const markAllRead = async () => {
        const snapshot = items;
        setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
        try {
            await api.put(API_ENDPOINTS.NOTIFICATIONS_READ_ALL(shopId));
            refreshUnread(shopId);
        } catch (err) {
            setItems(snapshot);
            toast.error(getErrorMessage(err));
        }
    };

    const open = (n: AppNotification) => {
        markRead(n);
        const d = n.notificationData;
        if (d?.target === 'TRANSACTION' && d.targetId) {
            navigate(`/transactions?receipt=${encodeURIComponent(d.targetId)}`);
        } else if (d?.url) {
            window.open(d.url, '_blank', 'noopener');
        }
    };

    const onRow = (n: AppNotification) => {
        if (isLandscape) { setSelectedId(n.id); markRead(n); } else open(n);
    };
    const hasTarget = (n: AppNotification | null) => !!(n?.notificationData?.target === 'TRANSACTION' && n.notificationData.targetId) || !!n?.notificationData?.url;
    const timeLabel = (iso: string) => formatRelativeShort(iso, i18n.language, true);

    return (
        <div className="flex h-full min-h-0 flex-1">
            <PageShell
                title={<>{t('notifications.title')} {unreadCount > 0 && <span className="text-ink-muted">{unreadCount}</span>}</>}
                backTo="/settings"
                className="lg:border-r lg:border-line-subtle"
                actions={<>
                    <IconButton variant="card" label={t('notifications.markAllRead')} onClick={markAllRead} disabled={unreadCount === 0} className="!text-ink-brand lg:!hidden"><CheckCheck /></IconButton>
                    <Button variant="ghost" size="sm" icon={<CheckCheck />} onClick={markAllRead} disabled={unreadCount === 0} className="hidden lg:inline-flex">{t('notifications.markAllRead')}</Button>
                </>}
            >
                <div className="flex flex-col gap-2 pb-5">
                    {error && <Alert variant="error" message={error} />}
                    {loading && items.length === 0 && [1, 2, 3].map((i) => <LoadingSkeleton key={i} height={72} />)}
                    {!loading && items.length === 0 && (
                        <div className="flex flex-col items-center gap-2 py-12 text-center text-ink-muted">
                            <Bell className="h-10 w-10 text-primary-200" />
                            <p className="text-sm font-bold">{t('notifications.empty')}</p>
                        </div>
                    )}
                    {groups.map(([label, rows]) => (
                        <React.Fragment key={label}>
                            <Overline className="px-0.5 pb-0.5 pt-2.5 first:pt-1">{label}</Overline>
                            {rows.map((n) => {
                                const active = isLandscape && n.id === selectedId;
                                return (
                                    <button
                                        key={n.id}
                                        type="button"
                                        onClick={() => onRow(n)}
                                        className={clsx(
                                            'flex items-start gap-3 rounded-lg px-3.5 py-3 text-left',
                                            active ? 'border-2 border-primary-500 bg-surface-card shadow-sm'
                                                : n.isRead ? 'border border-line-subtle bg-surface-card' : 'border-2 border-transparent bg-brandsoft',
                                        )}
                                    >
                                        <span className={clsx('mt-[7px] h-2 w-2 shrink-0 rounded-full', n.isRead ? 'bg-transparent' : 'bg-accent-500')} />
                                        <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                            <span className={clsx('text-sm leading-snug', n.isRead ? 'font-bold' : 'font-black')}>{n.title}</span>
                                            <span className="line-clamp-2 text-xs text-ink-secondary">{n.message}</span>
                                        </span>
                                        <span className="shrink-0 whitespace-nowrap text-[11px] text-ink-muted tnum">{timeLabel(n.createdAt)}</span>
                                    </button>
                                );
                            })}
                        </React.Fragment>
                    ))}
                    {!last && <Button variant="outline" onClick={() => load(page + 1, false)} loading={loading} className="mt-2 self-center">{t('common.loadMore')}</Button>}
                </div>
            </PageShell>

            <aside className="hidden w-[400px] shrink-0 flex-col gap-3.5 bg-surface-card p-[22px] lg:flex">
                {selected ? (
                    <>
                        <h2 className="text-[17px] font-black leading-snug">{selected.title}</h2>
                        <p className="text-[13px] text-ink-muted">{formatDateTime(selected.createdAt)}</p>
                        {selected.image && <img src={selected.image} alt="" className="w-full rounded-lg object-cover" />}
                        <p className="whitespace-pre-line border-y border-line-subtle py-3.5 text-sm text-ink-secondary">{selected.message}</p>
                        <div className="flex-1" />
                        {hasTarget(selected) && (
                            <Button variant="primary" size="lg" fullWidth iconRight={<ArrowRight />} onClick={() => open(selected)}>
                                {selected.notificationData?.target === 'TRANSACTION' ? t('notifications.viewTransaction') : t('notifications.open')}
                            </Button>
                        )}
                    </>
                ) : (
                    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-ink-muted">
                        <Bell className="h-10 w-10 text-primary-200" />
                        <p className="text-sm font-bold">{t('notifications.selectHint')}</p>
                    </div>
                )}
            </aside>
        </div>
    );
};

export default NotificationsPage;
